import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Search } from "lucide-react";
import {
  departmentsApi,
  departmentQueryKeys,
  type Department,
} from "../../shared/api/departments";
import { apiError } from "../../shared/api/client";
import { StatusPill } from "../../shared/ui/StatusPill";
import {
  useRowSelection,
  BulkDeleteBar,
  runBulkDelete,
} from "../../shared/ui/bulkSelect";
import { PageHeader } from "../../shared/ui/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { HIGHLIGHT_ROW_CLASS, useHighlightRow } from "../../shared/hooks/useHighlightRow";

/** Bề rộng cố định của nút Ngưng/Kích hoạt: hai nhãn dài khác nhau, không cố định thì cả bảng xê dịch. */
const TOGGLE_BUTTON_CLASS = "w-[5.5rem]";

/** Cài đặt Khoa — danh mục khoa của phòng khám + thao tác tạo/sửa/kích hoạt/ngưng. */
export function DepartmentsSettingsPage() {
  const highlight = useHighlightRow(); // #19683 — Audit log trỏ tới một dòng
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Department | "new" | null>(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<"" | "active" | "inactive">("");
  const [bulkError, setBulkError] = useState<string | null>(null);
  const sel = useRowSelection();

  const query = useQuery({
    queryKey: departmentQueryKeys.all(),
    queryFn: () => departmentsApi.listAll(),
  });

  const rows = useMemo(() => {
    return (query.data ?? [])
      .filter((d) => {
        if (statusFilter === "active" && !d.active) return false;
        if (statusFilter === "inactive" && d.active) return false;
        if (!q.trim()) return true;
        const needle = q.toLowerCase();
        return (
          d.name.toLowerCase().includes(needle) ||
          d.code.toLowerCase().includes(needle)
        );
      })
      .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, "vi"));
  }, [query.data, q, statusFilter]);

  const total = query.data?.length ?? 0;
  const activeCount = (query.data ?? []).filter((d) => d.active).length;

  function refresh() {
    qc.invalidateQueries({ queryKey: departmentQueryKeys.all() });
    qc.invalidateQueries({ queryKey: departmentQueryKeys.options() });
  }

  /**
   * Ngưng / Kích hoạt một khoa. Dòng vừa đổi được ghi thẳng vào cache từ phản hồi của server để
   * trạng thái lật ngay khi API trả lời, rồi mới invalidate để đồng bộ phần còn lại — trước đây
   * chỉ invalidate, nên bảng đứng yên thêm một vòng refetch sau khi bấm.
   */
  function applySaved(saved: Department) {
    qc.setQueryData<Department[]>(departmentQueryKeys.all(), (old) =>
      old?.map((x) => (x.id === saved.id ? saved : x)),
    );
    refresh();
  }
  const disable = useMutation({
    mutationFn: (id: string) => departmentsApi.disable(id),
    onSuccess: applySaved,
  });
  const enable = useMutation({
    mutationFn: (id: string) => departmentsApi.enable(id),
    onSuccess: applySaved,
  });
  /**
   * Chỉ khoá nút của ĐÚNG dòng đang gửi. `disable.isPending` dùng chung cho mọi dòng từng làm toàn
   * bộ cột "Ngưng" xám đi trong lúc một dòng chờ server — nhìn như màn hình lỗi.
   */
  const togglingId = disable.isPending
    ? disable.variables
    : enable.isPending
      ? enable.variables
      : null;
  const remove = useMutation({
    mutationFn: (id: string) => departmentsApi.remove(id),
    onSuccess: () => {
      setBulkError(null);
      refresh();
    },
    onError: (e: unknown) => setBulkError(apiError(e).detail),
  });
  const [bulkPending, setBulkPending] = useState(false);
  async function bulkDelete() {
    setBulkPending(true);
    const { failed } = await runBulkDelete([...sel.selectedIds], departmentsApi.remove);
    setBulkPending(false);
    sel.clear();
    refresh();
    setBulkError(
      failed.length ? `${failed.length} khoa không xoá được (đang được dùng).` : null,
    );
  }

  return (
    <div>
      <PageHeader
        title="Khoa"
        description="Danh mục khoa của phòng khám (VD: Khoa Nội, Khoa Nhi, Khoa Sản) — dùng để gán cho dịch vụ, bác sĩ và buồng khám, giúp gom nhóm và báo cáo theo khoa."
        actions={
          <Button type="button" onClick={() => setEditing("new")}>
            <Plus className="h-4 w-4" /> Tạo khoa
          </Button>
        }
      />

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Tổng số khoa</div>
            <div className="text-2xl font-bold">{total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Đang kích hoạt</div>
            <div className="text-2xl font-bold">{activeCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Đã ngưng</div>
            <div className="text-2xl font-bold">{total - activeCount}</div>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            type="search"
            className="pl-9"
            placeholder="Tìm theo tên hoặc mã khoa…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <select
          className="h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 sm:w-52"
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(e.target.value as "" | "active" | "inactive")
          }
          aria-label="Lọc theo trạng thái"
        >
          <option value="">Tất cả trạng thái</option>
          <option value="active">Đang kích hoạt</option>
          <option value="inactive">Đã ngưng</option>
        </select>
      </div>

      {query.isError && (
        <p className="mt-3 text-sm text-destructive">Không tải được danh mục khoa.</p>
      )}
      {query.isLoading && <p className="mt-3 text-sm text-muted-foreground">Đang tải…</p>}

      {bulkError && <p className="mt-3 text-sm text-destructive">{bulkError}</p>}
      {!query.isLoading && rows.length > 0 && (
        <>
          <div className="mt-4">
            <BulkDeleteBar
              count={sel.count}
              noun="khoa"
              pending={bulkPending}
              onClear={sel.clear}
              onDelete={bulkDelete}
            />
          </div>
          <div className="mt-2 rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-9 text-center">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-input align-middle accent-primary"
                      aria-label="Chọn tất cả"
                      checked={rows.length > 0 && rows.every((d) => sel.selectedIds.has(d.id))}
                      ref={(el) => {
                        if (el)
                          el.indeterminate =
                            rows.some((d) => sel.selectedIds.has(d.id)) &&
                            !rows.every((d) => sel.selectedIds.has(d.id));
                      }}
                      onChange={() => {
                        const next = new Set(sel.selectedIds);
                        if (rows.every((d) => next.has(d.id)))
                          rows.forEach((d) => next.delete(d.id));
                        else rows.forEach((d) => next.add(d.id));
                        sel.setSelectedIds(next);
                      }}
                    />
                  </TableHead>
                  <TableHead className="w-20 text-right">Thứ tự</TableHead>
                  <TableHead>Mã khoa</TableHead>
                  <TableHead>Tên khoa</TableHead>
                  <TableHead>Mô tả</TableHead>
                  <TableHead>Trạng thái</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((d) => (
                  <TableRow
                    key={d.id}
                    ref={highlight.isHighlighted(d.id) ? highlight.scrollRef : undefined}
                    className={highlight.isHighlighted(d.id) ? HIGHLIGHT_ROW_CLASS : undefined}
                  >
                    <TableCell className="text-center">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-input align-middle accent-primary"
                        aria-label="Chọn dòng"
                        checked={sel.selectedIds.has(d.id)}
                        onChange={() => sel.toggle(d.id)}
                      />
                    </TableCell>
                    <TableCell className="text-right">{d.sortOrder}</TableCell>
                    <TableCell className="font-mono">{d.code}</TableCell>
                    <TableCell>
                      <strong>{d.name}</strong>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {d.description || "—"}
                    </TableCell>
                    <TableCell>
                      {d.active ? (
                        <StatusPill tone="success">Đang kích hoạt</StatusPill>
                      ) : (
                        <StatusPill tone="neutral">Đã ngưng</StatusPill>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setEditing(d)}
                        >
                          Sửa
                        </Button>
                        {/* Cùng bề rộng cho "Ngưng" và "Kích hoạt" để cột không nhảy khi một dòng đổi trạng thái */}
                        {d.active ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className={TOGGLE_BUTTON_CLASS}
                            disabled={togglingId === d.id}
                            onClick={() => disable.mutate(d.id)}
                          >
                            Ngưng
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className={TOGGLE_BUTTON_CLASS}
                            disabled={togglingId === d.id}
                            onClick={() => enable.mutate(d.id)}
                          >
                            Kích hoạt
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          disabled={remove.isPending}
                          title="Xoá vĩnh viễn khoa (chỉ khi không còn tham chiếu)"
                          onClick={() => {
                            if (
                              confirm(`Xoá vĩnh viễn khoa "${d.name}"? Không thể hoàn tác.`)
                            )
                              remove.mutate(d.id);
                          }}
                        >
                          Xoá
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {!query.isLoading && rows.length === 0 && (
        <Card className="mt-4">
          <CardContent className="p-6 text-sm text-muted-foreground">
            {total === 0 ? (
              <>
                Chưa có khoa nào. Bấm <em>+ Tạo khoa</em> ở góc phải để bắt đầu.
              </>
            ) : (
              <>Không có khoa nào khớp bộ lọc.</>
            )}
          </CardContent>
        </Card>
      )}

      {editing && (
        <DepartmentFormModal
          item={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            refresh();
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function DepartmentFormModal({
  item,
  onClose,
  onSaved,
}: {
  item: Department | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [code, setCode] = useState(item?.code ?? "");
  const [name, setName] = useState(item?.name ?? "");
  const [description, setDescription] = useState(item?.description ?? "");
  const [sortOrder, setSortOrder] = useState<number | "">(item?.sortOrder ?? "");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      if (item) {
        return departmentsApi.update(item.id, {
          name: name.trim(),
          description: description.trim() || null,
          sortOrder: sortOrder === "" ? null : Number(sortOrder),
        });
      }
      return departmentsApi.create({
        code: code.trim(),
        name: name.trim(),
        description: description.trim() || null,
        sortOrder: sortOrder === "" ? null : Number(sortOrder),
      });
    },
    onSuccess: onSaved,
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-[520px]">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            save.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>{item ? "Sửa khoa" : "Tạo khoa mới"}</DialogTitle>
          </DialogHeader>

          <div className="mt-4 grid gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="dept-code">Mã khoa *</Label>
              <Input
                id="dept-code"
                required
                disabled={!!item}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="VD: NOI"
              />
              {item && (
                <p className="text-xs text-muted-foreground">
                  Không thay đổi mã sau khi tạo.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dept-name">Tên khoa *</Label>
              <Input
                id="dept-name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="VD: Khoa Nội"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dept-sort">Thứ tự</Label>
              <Input
                id="dept-sort"
                type="number"
                min={0}
                value={sortOrder}
                onChange={(e) =>
                  setSortOrder(e.target.value === "" ? "" : Number(e.target.value))
                }
                placeholder="100"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dept-desc">Mô tả</Label>
              <Textarea
                id="dept-desc"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>

          {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Đóng
            </Button>
            <Button
              type="submit"
              disabled={save.isPending || !name.trim() || (!item && !code.trim())}
            >
              {save.isPending ? "Đang lưu…" : item ? "Lưu thay đổi" : "Tạo khoa"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
